// Image Generation API client (I3 Web).
//
// Security contract (ADR-028, A-025/A-026): the browser talks ONLY to the
// RealityAI authenticated API. No provider URL, no Comfy semantics, no
// filesystem paths. Image bytes are fetched with the Bearer token and
// rendered via object URLs — never a public provider path.
import { ApiError, getToken, request } from "@/lib/api";

export interface ImageGenPreset {
  code: string;
  output_width: number;
  output_height: number;
  upscale: boolean;
}

// V2 semantic contract (ADR-033): operations the ACTIVE production
// provider can complete. The backend registry is the single source of
// truth — the UI never hard-codes provider capabilities or limits.
export interface ImageGenOperation {
  code: string;
  status: "available";
}

export interface ImageGenCapability {
  feature_enabled: boolean;
  entitled: boolean;
  reason: string;
  // Daily quota view (Owner 15-10). unlimited=true => no daily limit
  // (admin/staff); daily_limit/remaining_today are null in that case.
  unlimited?: boolean;
  daily_limit?: number | null;
  used_today?: number;
  remaining_today?: number | null;
  presets: ImageGenPreset[];
  // V2: available-only operation list (candidate/disabled are never
  // sent to end-user clients). Absent on pre-V2 backends -> the UI
  // falls back to text_to_image only (the production FLUX.2 truth).
  operations?: ImageGenOperation[];
  max_reference_images?: number;
}

export type ImageGenStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "expired";

export interface ImageGenJob {
  id: string;
  status: ImageGenStatus;
  preset: string;
  prompt: string;
  requested_output: { width: number; height: number };
  seed: number | null;
  project_id: string | null;
  chat_id: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  error: { code: string; message: string } | null;
  has_result: boolean;
  // V2 additive (null/absent on pre-V2 rows — treat as text_to_image).
  operation?: string | null;
  attachments?: { file_id: string; role: string }[] | null;
  layout?: { items: { label: string; x: number; y: number; w: number; h: number }[] } | null;
  // V2 continue-editing: the caller's own result UserFile id (reuse as
  // attachment/reference WITHOUT re-upload).
  result_file_id?: string | null;
}

/** Capability probe. Feature gate OFF -> backend 404 (no disclosure):
 *  callers treat 404 as "feature unavailable", not as an error. */
export async function fetchImageGenCapability(): Promise<ImageGenCapability | null> {
  try {
    return await request<ImageGenCapability>("/api/images/generations/capability");
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function createGeneration(params: {
  prompt: string;
  preset: string;
  seed?: number | null;
  idempotencyKey?: string;
  // V2 semantic contract (all optional; the backend infers when omitted).
  operation?: string;
  attachments?: { file_id: string; role: string }[];
  layout?: { items: { label: string; x: number; y: number; w: number; h: number }[] };
}): Promise<ImageGenJob> {
  const headers: Record<string, string> = {};
  if (params.idempotencyKey) headers["Idempotency-Key"] = params.idempotencyKey;
  return request<ImageGenJob>("/api/images/generations", {
    method: "POST",
    headers,
    body: JSON.stringify({
      prompt: params.prompt,
      preset: params.preset,
      seed: params.seed ?? null,
      ...(params.operation ? { operation: params.operation } : {}),
      ...(params.attachments && params.attachments.length
        ? { attachments: params.attachments }
        : {}),
      ...(params.layout ? { layout: params.layout } : {}),
    }),
  });
}

export async function listGenerations(limit = 20): Promise<ImageGenJob[]> {
  return request<ImageGenJob[]>(`/api/images/generations?limit=${limit}`);
}

export async function fetchGeneration(jobId: string): Promise<ImageGenJob> {
  return request<ImageGenJob>(`/api/images/generations/${jobId}`);
}

export async function cancelGeneration(jobId: string): Promise<ImageGenJob> {
  return request<ImageGenJob>(`/api/images/generations/${jobId}/cancel`, {
    method: "POST",
  });
}

/** Authorized image bytes via the RealityAI storage route (never a provider
 *  path). Fetch with Bearer, return an object URL; caller must revoke it. */
export async function fetchGenerationImageBlobUrl(jobId: string): Promise<string> {
  const blob = await fetchGenerationImageBlob(jobId);
  return URL.createObjectURL(blob);
}

/** Authorized image bytes as a Blob (for copy-to-clipboard / Web Share). */
export async function fetchGenerationImageBlob(jobId: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`/api/images/generations/${jobId}/image`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, "Результат недоступен");
  return res.blob();
}

/** Download the result through the authorized route (blob + <a download>). */
export async function downloadGenerationImage(jobId: string, filename: string): Promise<void> {
  const url = await fetchGenerationImageBlobUrl(jobId);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the click time to start before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export const PRESET_LABELS: Record<string, string> = {
  square_768: "768 × 768",
  landscape_576: "1024 × 576",
  landscape_768: "1024 × 768",
  portrait_768: "768 × 1024",
  square_1024: "1024 × 1024",
  fhd_safe: "Full HD (Safe)",
};

export const STATUS_LABELS: Record<ImageGenStatus, string> = {
  queued: "В очереди",
  running: "Генерация…",
  succeeded: "Готово",
  failed: "Ошибка",
  cancelled: "Отменено",
  expired: "Истекло",
};
